import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import arLocale from '@fullcalendar/core/locales/ar';
import enLocale from '@fullcalendar/core/locales/en-gb';
import frLocale from '@fullcalendar/core/locales/fr';
import { LocaleInput } from '@fullcalendar/core';

import {
  AcademicAppointment,
  SessionKind,
} from '../../interfaces/academic-appointment';
import { LocalizedLabelService } from '../utils/localized-label.service';

/**
 * Présentation partagée des séances : couleurs, icônes, libellés, locale du
 * calendrier. Centralisé pour que l'agenda élève, le calendrier prof et les
 * tableaux de bord parlent le même langage visuel.
 */
@Injectable({ providedIn: 'root' })
export class SessionViewService {
  private readonly translate = inject(TranslateService);
  private readonly labels = inject(LocalizedLabelService);

  /**
   * Libellé de la matière d'une séance, dans la langue active.
   * Passe par le service partagé pour que l'agenda, les calendriers et les
   * tableaux de bord affichent tous le même nom.
   */
  subjectLabel(a: AcademicAppointment | null | undefined): string {
    return this.labels.pickPrefixed(a as unknown as Record<string, unknown>, 'subjectName');
  }

  /** Nombre de teintes disponibles dans la feuille de style (`.sess-c1` … `.sess-c8`). */
  private static readonly PALETTE_SIZE = 8;

  /**
   * Teinte d'une séance, dérivée de sa matière : la même matière garde la même
   * couleur d'un écran à l'autre, sans table de correspondance à maintenir.
   * Les séances sans matière retombent sur la dernière teinte, neutre.
   */
  colorClass(a: { subjectId?: number | null } | null | undefined): string {
    if (!a?.subjectId) return 'sess-c0';
    const index = (Math.abs(a.subjectId) % SessionViewService.PALETTE_SIZE) + 1;
    return `sess-c${index}`;
  }

  /** Icône FontAwesome correspondant à la nature de la séance. */
  kindIcon(kind: SessionKind | null | undefined): string {
    switch (kind) {
      case 'Exam':     return 'fa-solid fa-file-pen';
      case 'Revision': return 'fa-solid fa-rotate-right';
      case 'Tutoring': return 'fa-solid fa-user-graduate';
      case 'Course':   return 'fa-solid fa-chalkboard-user';
      default:         return 'fa-solid fa-calendar-day';
    }
  }

  /** Clé i18n du libellé de la nature de séance. */
  kindLabelKey(kind: SessionKind | null | undefined): string {
    switch (kind) {
      case 'Exam':     return 'CALENDAR.KIND.EXAM';
      case 'Revision': return 'CALENDAR.KIND.REVISION';
      case 'Tutoring': return 'CALENDAR.KIND.TUTORING';
      case 'Course':   return 'CALENDAR.KIND.COURSE';
      default:         return 'CALENDAR.KIND.OTHER';
    }
  }

  /** Locale FullCalendar alignée sur la langue active de l'application. */
  calendarLocale(): LocaleInput {
    switch (this.translate.currentLang) {
      case 'fr': return frLocale;
      case 'en': return enLocale;
      default:   return arLocale;
    }
  }

  /** L'arabe est la seule langue RTL du produit. */
  isRtl(): boolean {
    return this.translate.currentLang === 'ar';
  }
}
